import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { DownloadOverlay } from '../player/components/DownloadOverlay';
import { FullscreenButton } from '../player/components/FullscreenButton';
import { ViewerToolbarOverflowMenu } from '../player/components/ViewerToolbarOverflowMenu';
import { useContainerWidth } from '../player/hooks/useContainerWidth';
import { SidePanel } from '../player/components/SidePanel';
import { requestFullscreen, exitFullscreen as exitFs, getFullscreenElement, addFullscreenChangeListener, } from '../player/utils/fullscreenCompat';
import { AnnotationEditor } from '../annotations/AnnotationEditor';
import { AnnotationViewer } from '../annotations/AnnotationViewer';
const PDF_WORKER_SRC = 'js/third_party/pdf/pdf.worker.js';
const MIN_SCALE = 0.05;
const MAX_SCALE = 10.0;
const DEFAULT_FILE_TYPE_NAME = 'PDF Document';
const FULLSCREEN_CLASS = 'hekate-player-fullscreen-wrapper';
const DOCUMENT_VIEWER_KEYBOARD_SHORTCUTS = [
    { key: '←', description: 'Previous page' },
    { key: '→', description: 'Next page' },
    { key: 'Home', description: 'First page' },
    { key: 'End', description: 'Last page' },
    { key: 'Shift + F', description: 'Toggle fullscreen' },
];
/** Read the `?page=` query parameter from the current URL. */
function pageFromUrl() {
    if (typeof URLSearchParams === 'undefined' || typeof window === 'undefined')
        return null;
    try {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('page');
        if (!raw)
            return null;
        const n = parseInt(raw, 10);
        return Number.isFinite(n) && n > 0 ? n : null;
    }
    catch {
        return null;
    }
}
/**
 * Lookup PDF.js on the window object. PDF.js is normally loaded as a
 * `<script>` tag by the host page, but only when the primary file is a
 * PDF. When a PDF is opened as a compare column alongside a non-PDF
 * primary file, the script isn't included. In that case, dynamically
 * load it.
 */
function getPdfJs() {
    if (typeof window === 'undefined')
        return null;
    return window['pdfjs-dist/build/pdf'] || null;
}
let pdfJsLoadPromise = null;
function loadPdfJs() {
    const existing = getPdfJs();
    if (existing)
        return Promise.resolve(existing);
    if (pdfJsLoadPromise)
        return pdfJsLoadPromise;
    pdfJsLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'js/third_party/pdf/pdf.js';
        // Timeout in case onload/onerror don't fire (e.g. jsdom in tests)
        const timeout = setTimeout(() => {
            const lib = getPdfJs();
            if (lib)
                resolve(lib);
            else
                reject(new Error('PDF.js library is not loaded'));
        }, 5000);
        script.onload = () => {
            clearTimeout(timeout);
            const lib = getPdfJs();
            if (lib) {
                resolve(lib);
            }
            else {
                reject(new Error('PDF.js loaded but global not found'));
            }
        };
        script.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Failed to load PDF.js'));
        };
        document.head.appendChild(script);
    });
    // Reset the cached promise on failure so retries create a fresh script
    pdfJsLoadPromise.catch(() => { pdfJsLoadPromise = null; });
    return pdfJsLoadPromise;
}
/**
 * React PDF viewer built on PDF.js. Owns the PDF.js document, the canvas,
 * and the zoom/page controls. Falls back to the shared `DownloadOverlay`
 * component if PDF.js fails to load the document.
 */
const HekateDocumentViewer = forwardRef(function HekateDocumentViewer(props, ref) {
    const { fileUrl, fileTitle, width, height, initialPage, canDownload, downloadUrl, fileTypeName, fileIconUrl, fileSize, onFullscreenChange, sidePanelElementId, sidePanelWidth: sidePanelWidthProp, sidePanelTooltip, } = props;
    const outerContainerRef = useRef(null);
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const canvasWrapperRef = useRef(null);
    // Annotation state — refs so effects can access without re-running
    const annotationEditorSyncRef = useRef(null);
    const annotationEditorHandleRef = useRef(null);
    const annotationViewerHandleRef = useRef(null);
    const syncAnnotationRootsRef = useRef(null);
    // Fullscreen state
    const [isFullscreen, setIsFullscreen] = useState(false);
    const isFullscreenRef = useRef(false);
    isFullscreenRef.current = isFullscreen;
    // Side panel state
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [sidePanelHasElement, setSidePanelHasElement] = useState(false);
    const [sidePanelWidth, setSidePanelWidth] = useState(sidePanelWidthProp ?? 400);
    const sidePanelContentRef = useRef(null);
    const sidePanelAdoptedElRef = useRef(null);
    const sidePanelElementOriginRef = useRef(null);
    // PDF.js document is mutable, doesn't need to drive renders.
    const pdfDocRef = useRef(null);
    // Page-event subscribers.
    const pageSubscribersRef = useRef([]);
    const isFirstPageNotifyRef = useRef(true);
    const fullscreenChangedRef = useRef(false);
    const scaleRef = useRef(1.0);
    // Track an in-flight render so we can queue at most one follow-up.
    const isRenderingRef = useRef(false);
    const pendingPageRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(initialPage || pageFromUrl() || 1);
    const currentPageRef = useRef(initialPage || pageFromUrl() || 1);
    const [totalPages, setTotalPages] = useState(1);
    const [scale, setScale] = useState(1.0);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    useEffect(() => { scaleRef.current = scale; }, [scale]);
    // ── Responsive overflow menu ──
    const containerWidth = useContainerWidth(outerContainerRef);
    // Fit scale: the zoom level that fits the current page within the
    // container. Computed on load and updated by ResizeObserver.
    const fitScaleRef = useRef(1.0);
    const fitScaleInitializedRef = useRef(false);
    // The page-input <input> is a controlled string so users can clear it
    // mid-edit without snapping back to the committed page.
    const [pageInput, setPageInput] = useState(String(initialPage || pageFromUrl() || 1));
    // ----------------------------------------------------------------------
    // Page rendering
    // ----------------------------------------------------------------------
    const renderPage = useCallback((pageNum, atScale) => {
        const pdfDoc = pdfDocRef.current;
        const canvas = canvasRef.current;
        if (!pdfDoc || !canvas)
            return;
        isRenderingRef.current = true;
        pdfDoc.getPage(pageNum).then((page) => {
            const viewport = page.getViewport({ scale: atScale });
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                isRenderingRef.current = false;
                return;
            }
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            const renderTask = page.render({ canvasContext: ctx, viewport });
            renderTask.promise.then(() => {
                isRenderingRef.current = false;
                // Re-sync annotation overlays after canvas dimensions change.
                syncAnnotationRootsRef.current?.();
                annotationEditorSyncRef.current?.();
                if (pendingPageRef.current !== null) {
                    const next = pendingPageRef.current;
                    pendingPageRef.current = null;
                    renderPage(next, atScale);
                }
            }, () => {
                isRenderingRef.current = false;
            });
        });
    }, []);
    const queueRenderPage = useCallback((pageNum, atScale) => {
        if (isRenderingRef.current) {
            pendingPageRef.current = pageNum;
        }
        else {
            renderPage(pageNum, atScale);
        }
    }, [renderPage]);
    // ----------------------------------------------------------------------
    // Load the PDF document on mount
    // ----------------------------------------------------------------------
    useEffect(() => {
        let cancelled = false;
        let loadingTask = null;
        loadPdfJs().then((pdfjsLib) => {
            if (cancelled)
                return;
            pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
            loadingTask = pdfjsLib.getDocument({ url: fileUrl, withCredentials: true });
            return loadingTask.promise;
        }).then((pdf) => {
            if (cancelled || !pdf)
                return;
            pdfDocRef.current = pdf;
            setTotalPages(pdf.numPages);
            // Clamp the initial page to the document range.
            const start = Math.min(Math.max(currentPage, 1), pdf.numPages);
            currentPageRef.current = start;
            setCurrentPage(start);
            setPageInput(String(start));
            setIsLoading(false);
            // Compute fit scale from the first page's dimensions and the
            // container's available space, so the PDF fits on screen by
            // default instead of overflowing at native resolution.
            pdf.getPage(start).then((page) => {
                if (cancelled)
                    return;
                const nativeVp = page.getViewport({ scale: 1.0 });
                const container = containerRef.current;
                if (container && container.clientWidth > 0) {
                    const availW = container.clientWidth;
                    const availH = container.clientHeight;
                    const fs = Math.min(availW / nativeVp.width, availH > 0 ? availH / nativeVp.height : availW / nativeVp.width);
                    if (Number.isFinite(fs) && fs > 0) {
                        fitScaleRef.current = fs;
                        fitScaleInitializedRef.current = true;
                        setScale(fs); // triggers render via the scale useEffect
                    }
                    else {
                        renderPage(start, scale);
                    }
                }
                else {
                    renderPage(start, scale);
                }
            });
        }).catch((reason) => {
            if (cancelled)
                return;
            // eslint-disable-next-line no-console
            console.error('PDF load failed:', reason);
            setLoadError(String(reason));
            setIsLoading(false);
        });
        return () => {
            cancelled = true;
            if (loadingTask && typeof loadingTask.destroy === 'function') {
                try {
                    loadingTask.destroy();
                }
                catch { /* ignore */ }
            }
            if (pdfDocRef.current && typeof pdfDocRef.current.destroy === 'function') {
                try {
                    pdfDocRef.current.destroy();
                }
                catch { /* ignore */ }
                pdfDocRef.current = null;
            }
        };
        // We deliberately only run this on mount; subsequent fileUrl changes
        // would mean a different file, which the host re-mounts via loadFile().
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Re-render whenever the current page or zoom changes.
    useEffect(() => {
        if (!pdfDocRef.current)
            return;
        queueRenderPage(currentPage, scale);
    }, [currentPage, scale, queueRenderPage]);
    // Notify page-event subscribers when the page or total changes.
    useEffect(() => {
        if (isFirstPageNotifyRef.current) {
            isFirstPageNotifyRef.current = false;
            return;
        }
        for (const sub of pageSubscribersRef.current) {
            sub({ type: 'page_change', page: currentPage, totalPages });
        }
    }, [currentPage, totalPages]);
    // ----------------------------------------------------------------------
    // Navigation + zoom helpers
    // ----------------------------------------------------------------------
    const goToPage = useCallback((num) => {
        const pdfDoc = pdfDocRef.current;
        if (!pdfDoc)
            return;
        let next = num;
        if (!Number.isFinite(next) || next < 1)
            next = 1;
        if (next > pdfDoc.numPages)
            next = pdfDoc.numPages;
        if (next === currentPageRef.current)
            return;
        // If an annotation editor is open, dismiss it (prompt if dirty)
        const editorHandle = annotationEditorHandleRef.current;
        if (editorHandle) {
            if (editorHandle.api && typeof editorHandle.api.isDirty === 'function' && editorHandle.api.isDirty()) {
                if (!window.confirm('You have unsaved annotation changes. Discard them?')) {
                    return;
                }
            }
            editorHandle.unmount();
            annotationEditorHandleRef.current = null;
            annotationEditorSyncRef.current = null;
            window.annotationEditorHandle = null;
            if (typeof window.exitAnnotationMode === 'function') {
                window.exitAnnotationMode();
            }
        }
        // Dismiss any annotation viewer when changing pages
        const viewerHandle = annotationViewerHandleRef.current;
        if (viewerHandle) {
            viewerHandle.unmount();
            annotationViewerHandleRef.current = null;
        }
        currentPageRef.current = next;
        setCurrentPage(next);
        setPageInput(String(next));
    }, []);
    const nextPage = useCallback(() => {
        const pdfDoc = pdfDocRef.current;
        if (!pdfDoc)
            return;
        if (currentPageRef.current >= pdfDoc.numPages)
            return;
        goToPage(currentPageRef.current + 1);
    }, [goToPage]);
    const prevPage = useCallback(() => {
        if (currentPageRef.current <= 1)
            return;
        goToPage(currentPageRef.current - 1);
    }, [goToPage]);
    const setZoom = useCallback((nextScale) => {
        if (nextScale <= MIN_SCALE)
            return;
        if (nextScale >= MAX_SCALE)
            return;
        setScale(nextScale);
    }, []);
    const incrementZoom = useCallback((delta) => {
        setScale((prev) => {
            const next = prev + delta;
            if (next <= MIN_SCALE || next >= MAX_SCALE)
                return prev;
            return next;
        });
    }, []);
    // Recompute fitScale when the container resizes (e.g., column width
    // changes, fullscreen toggle). Don't auto-adjust the current scale —
    // just update the ref so zoomToFit uses the latest dimensions.
    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === 'undefined')
            return;
        const observer = new ResizeObserver(() => {
            const pdfDoc = pdfDocRef.current;
            if (!pdfDoc)
                return;
            pdfDoc.getPage(currentPage).then((page) => {
                const nativeVp = page.getViewport({ scale: 1.0 });
                const availW = container.clientWidth;
                const availH = container.clientHeight;
                fitScaleRef.current = Math.min(availW / nativeVp.width, availH > 0 ? availH / nativeVp.height : availW / nativeVp.width);
                if (fullscreenChangedRef.current) {
                    zoomToFit();
                    fullscreenChangedRef.current = false;
                }
            });
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [currentPage]);
    const zoomToFit = useCallback(() => {
        setScale(fitScaleRef.current);
    }, []);
    // Build overflow menu items based on which breakpoint tier is active.
    // Mirrors the CSS container query breakpoints in document-viewer.scss.
    const overflowItems = useMemo(() => {
        const items = [];
        if (containerWidth <= 500) {
            items.push({
                id: 'zoom-fit',
                type: 'action',
                label: 'Zoom to Fit',
                onClick: () => zoomToFit(),
            });
        }
        if (containerWidth <= 400) {
            if (items.length > 0)
                items.push({ type: 'separator', id: 'sep-zoom-inout' });
            items.push({
                id: 'zoom-out',
                type: 'action',
                label: 'Zoom Out',
                onClick: () => incrementZoom(-0.1),
            });
            items.push({
                id: 'zoom-in',
                type: 'action',
                label: 'Zoom In',
                onClick: () => incrementZoom(0.1),
            });
        }
        if (containerWidth <= 300) {
            if (items.length > 0)
                items.push({ type: 'separator', id: 'sep-zoom-pct' });
            items.push({
                id: 'zoom-reset',
                type: 'action',
                label: `Reset Zoom (${Math.round(scale * 100)}%)`,
                onClick: () => setZoom(1.0),
            });
        }
        return items;
    }, [containerWidth, scale, zoomToFit, incrementZoom, setZoom]);
    // Ctrl/Cmd-scroll to zoom.
    useEffect(() => {
        const el = containerRef.current;
        if (!el)
            return;
        const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
        const onWheel = (e) => {
            if (e.metaKey || (!isMac && e.ctrlKey)) {
                e.preventDefault();
                incrementZoom(e.deltaY * -0.005);
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [incrementZoom]);
    // ----------------------------------------------------------------------
    // Fullscreen handling
    // ----------------------------------------------------------------------
    const toggleFullscreen = useCallback(() => {
        const el = outerContainerRef.current;
        if (!el)
            return;
        if (getFullscreenElement()) {
            exitFs();
        }
        else {
            requestFullscreen(el);
        }
    }, []);
    // Arrow keys for page navigation, Home/End for first/last page,
    // Shift+F for fullscreen toggle.
    useEffect(() => {
        const el = outerContainerRef.current;
        if (!el)
            return;
        const onKeyDown = (e) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
                return;
            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    e.stopPropagation();
                    prevPage();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    e.stopPropagation();
                    nextPage();
                    break;
                case 'Home':
                    e.preventDefault();
                    e.stopPropagation();
                    goToPage(1);
                    break;
                case 'End':
                    e.preventDefault();
                    e.stopPropagation();
                    goToPage(pdfDocRef.current ? pdfDocRef.current.numPages : 1);
                    break;
                case 'F':
                case 'f':
                    if (e.shiftKey && !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFullscreen();
                    }
                    break;
            }
        };
        el.addEventListener('keydown', onKeyDown);
        return () => el.removeEventListener('keydown', onKeyDown);
    }, [prevPage, nextPage, goToPage, toggleFullscreen]);
    useEffect(() => {
        const handleChange = () => {
            const isFs = !!getFullscreenElement();
            setIsFullscreen(isFs);
            onFullscreenChange?.(isFs);
            fullscreenChangedRef.current = true;
            const el = outerContainerRef.current;
            if (el) {
                if (isFs) {
                    el.classList.add(FULLSCREEN_CLASS);
                }
                else {
                    el.classList.remove(FULLSCREEN_CLASS);
                }
            }
        };
        const cleanup = addFullscreenChangeListener(handleChange);
        return () => {
            cleanup();
            outerContainerRef.current?.classList.remove(FULLSCREEN_CLASS);
        };
    }, [onFullscreenChange]);
    // Restore side panel element and close panel when leaving fullscreen
    useEffect(() => {
        if (!isFullscreen) {
            setSidePanelOpen(false);
            const origin = sidePanelElementOriginRef.current;
            const el = sidePanelAdoptedElRef.current;
            if (origin && el) {
                if (origin.nextSibling) {
                    origin.parent.insertBefore(el, origin.nextSibling);
                }
                else {
                    origin.parent.appendChild(el);
                }
                sidePanelElementOriginRef.current = null;
                sidePanelAdoptedElRef.current = null;
            }
        }
    }, [isFullscreen]);
    // Sync sidePanelWidth prop to state
    useEffect(() => {
        if (sidePanelWidthProp != null && sidePanelWidthProp > 0) {
            setSidePanelWidth(sidePanelWidthProp);
        }
    }, [sidePanelWidthProp]);
    // Toggle wrapper class to reserve space for the side panel when open
    useEffect(() => {
        const el = outerContainerRef.current;
        if (!el)
            return;
        el.style.setProperty('--side-panel-w', `${sidePanelWidth}px`);
        if (isFullscreen && sidePanelOpen) {
            el.classList.add('hekate-player-sidepanel-active');
        }
        else {
            el.classList.remove('hekate-player-sidepanel-active');
        }
    }, [isFullscreen, sidePanelOpen, sidePanelWidth]);
    // Helper: adopt a DOM element into the side panel (or restore the current one)
    const adoptSidePanelElement = useCallback((elementId) => {
        const panel = sidePanelContentRef.current;
        // Restore any previously adopted element first
        const origin = sidePanelElementOriginRef.current;
        const adopted = sidePanelAdoptedElRef.current;
        if (origin && adopted) {
            if (origin.nextSibling) {
                origin.parent.insertBefore(adopted, origin.nextSibling);
            }
            else {
                origin.parent.appendChild(adopted);
            }
            sidePanelElementOriginRef.current = null;
            sidePanelAdoptedElRef.current = null;
        }
        if (!elementId) {
            setSidePanelHasElement(false);
            return;
        }
        setSidePanelHasElement(true);
        if (!panel)
            return;
        const el = document.getElementById(elementId);
        if (!el || !el.parentElement)
            return;
        if (el.parentElement === panel)
            return;
        sidePanelElementOriginRef.current = {
            parent: el.parentElement,
            nextSibling: el.nextSibling,
        };
        sidePanelAdoptedElRef.current = el;
        panel.appendChild(el);
    }, []);
    // Sync sidePanelElementId prop — adopt when entering fullscreen
    useEffect(() => {
        if (isFullscreen) {
            adoptSidePanelElement(sidePanelElementId ?? null);
        }
    }, [sidePanelElementId, isFullscreen, adoptSidePanelElement]);
    // Unmount cleanup — restore any adopted side panel element
    useEffect(() => {
        return () => {
            const origin = sidePanelElementOriginRef.current;
            const el = sidePanelAdoptedElRef.current;
            if (origin && el) {
                if (origin.nextSibling && origin.nextSibling.parentNode === origin.parent) {
                    origin.parent.insertBefore(el, origin.nextSibling);
                }
                else {
                    origin.parent.appendChild(el);
                }
                sidePanelElementOriginRef.current = null;
                sidePanelAdoptedElRef.current = null;
            }
        };
    }, []);
    // ----------------------------------------------------------------------
    // Annotation integration
    // ----------------------------------------------------------------------
    // Sync annotation root dimensions with the PDF canvas whenever the
    // canvas size changes (zoom, page change). The annotation root is
    // positioned absolute inside the canvas wrapper, sized to match the
    // canvas pixel dimensions (CSS pixels, not canvas buffer pixels).
    const syncAnnotationRoots = useCallback(() => {
        const wrapper = canvasWrapperRef.current;
        const canvas = canvasRef.current;
        if (!wrapper || !canvas)
            return;
        const roots = wrapper.querySelectorAll('[data-annotation-root]');
        roots.forEach((el) => {
            const htmlEl = el;
            htmlEl.style.width = canvas.width + 'px';
            htmlEl.style.height = canvas.height + 'px';
            htmlEl.dataset.zoom = String(scale);
        });
    }, [scale]);
    // Keep ref in sync so renderPage callback can call it
    syncAnnotationRootsRef.current = syncAnnotationRoots;
    // Re-sync annotation roots when scale or page changes
    useEffect(() => {
        syncAnnotationRoots();
        // Also re-sync the editor host position (if an editor is open)
        annotationEditorSyncRef.current?.();
    }, [scale, currentPage, syncAnnotationRoots]);
    // Re-sync editor host position on fullscreen changes
    useEffect(() => {
        // Delay to let the fullscreen transition complete
        const timer = setTimeout(() => {
            annotationEditorSyncRef.current?.();
        }, 100);
        return () => clearTimeout(timer);
    }, [isFullscreen]);
    const showAnnotationEditor = useCallback((options = {}) => {
        const outer = outerContainerRef.current;
        const wrapper = canvasWrapperRef.current;
        const canvas = canvasRef.current;
        if (!outer || !wrapper) {
            return { unmount: () => { }, get api() { return null; } };
        }
        // Lock the outer container height so the toolbar (inserted as a
        // flex child with order:-1) shrinks the content area instead of
        // growing the container.
        outer.style.height = outer.offsetHeight + 'px';
        // Mount annotation root inside the canvas wrapper so it naturally
        // scrolls and aligns with the canvas. The toolbarPortalTarget prop
        // directs the toolbar to the outer flex container.
        const root_el = document.createElement('div');
        root_el.setAttribute('data-annotation-root', 'editor');
        root_el.style.position = 'absolute';
        root_el.style.top = '0';
        root_el.style.left = '0';
        root_el.style.width = '100%';
        root_el.style.height = '100%';
        root_el.dataset.zoom = String(scale);
        wrapper.appendChild(root_el);
        outer.classList.add('hekate-annotation-active');
        const syncAnnotationZoom = () => {
            root_el.dataset.zoom = String(scaleRef.current);
            if (canvas) {
                root_el.style.width = canvas.width + 'px';
                root_el.style.height = canvas.height + 'px';
            }
        };
        annotationEditorSyncRef.current = syncAnnotationZoom;
        const targetRefObj = { current: root_el };
        const apiRefObj = React.createRef();
        const reactRoot = createRoot(root_el);
        const { initialShapes, isEditing, onSave, onDiscard, isFullscreen: isFs, defaultColor } = options;
        flushSync(() => {
            reactRoot.render(React.createElement(AnnotationEditor, {
                ref: apiRefObj,
                targetRef: targetRefObj,
                toolbarPortalTarget: outer,
                initialShapes,
                isEditing,
                onSave,
                onDiscard,
                isFullscreen: isFs,
                defaultColor,
            }));
        });
        const handle = {
            unmount: () => {
                // Clone the toolbar for slide-out animation before React unmount
                const toolbar = outer.querySelector(':scope > .hekate-annotation-toolbar');
                let toolbarClone = null;
                if (toolbar) {
                    toolbarClone = toolbar.cloneNode(true);
                    toolbarClone.style.animation = 'hekate-toolbar-slide-out 0.3s ease forwards';
                    toolbar.parentElement.insertBefore(toolbarClone, toolbar);
                }
                reactRoot.unmount();
                root_el.remove();
                outer.classList.remove('hekate-annotation-active');
                annotationEditorSyncRef.current = null;
                annotationEditorHandleRef.current = null;
                if (toolbarClone) {
                    let cleaned = false;
                    const cleanup = () => {
                        if (cleaned)
                            return;
                        cleaned = true;
                        toolbarClone.remove();
                        outer.style.height = '';
                    };
                    toolbarClone.addEventListener('animationend', cleanup, { once: true });
                    setTimeout(cleanup, 400);
                }
                else {
                    outer.style.height = '';
                }
            },
            get api() {
                return apiRefObj.current;
            },
        };
        annotationEditorHandleRef.current = handle;
        return handle;
    }, [scale]);
    const showAnnotationViewer = useCallback((shapes) => {
        const wrapper = canvasWrapperRef.current;
        if (!wrapper) {
            return {
                unmount: () => { },
                get api() { return null; },
                updateShapes: () => { },
            };
        }
        const root_el = document.createElement('div');
        root_el.setAttribute('data-annotation-root', 'viewer');
        root_el.style.position = 'absolute';
        root_el.style.top = '0';
        root_el.style.left = '0';
        const canvas = canvasRef.current;
        if (canvas) {
            root_el.style.width = canvas.width + 'px';
            root_el.style.height = canvas.height + 'px';
            root_el.dataset.zoom = String(scale);
        }
        wrapper.appendChild(root_el);
        wrapper.classList.add('hekate-annotation-active');
        const targetRefObj = { current: root_el };
        const apiRefObj = React.createRef();
        const reactRoot = createRoot(root_el);
        function renderViewer(currentShapes) {
            flushSync(() => {
                reactRoot.render(React.createElement(AnnotationViewer, {
                    ref: apiRefObj,
                    targetRef: targetRefObj,
                    shapes: currentShapes,
                }));
            });
        }
        renderViewer(shapes);
        const handle = {
            unmount: () => {
                reactRoot.unmount();
                root_el.remove();
                wrapper.classList.remove('hekate-annotation-active');
                annotationViewerHandleRef.current = null;
            },
            get api() {
                return apiRefObj.current;
            },
            updateShapes: (newShapes) => {
                renderViewer(newShapes);
            },
        };
        annotationViewerHandleRef.current = handle;
        return handle;
    }, [scale]);
    // ----------------------------------------------------------------------
    // Imperative API
    // ----------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
        goToPage,
        nextPage,
        prevPage,
        setZoom,
        getCurrentPage: () => currentPageRef.current,
        getTotalPages: () => totalPages,
        subscribeToPageEvents: (cb) => {
            pageSubscribersRef.current = [...pageSubscribersRef.current, cb];
            return { unsubscribe: () => { pageSubscribersRef.current = pageSubscribersRef.current.filter(item => item !== cb); } };
        },
        isFullscreen: () => isFullscreenRef.current,
        enterFullscreen: () => {
            const el = outerContainerRef.current;
            if (el && !getFullscreenElement()) {
                requestFullscreen(el);
            }
        },
        exitFullscreen: () => {
            if (getFullscreenElement())
                exitFs();
        },
        setSidePanelElement: (elementId) => {
            adoptSidePanelElement(elementId);
        },
        setSidePanelOpen: (open) => {
            setSidePanelOpen(open);
        },
        setSidePanelWidth: (width) => {
            setSidePanelWidth(width);
        },
        getContainerElement: () => outerContainerRef.current,
        getKeyboardShortcuts: () => DOCUMENT_VIEWER_KEYBOARD_SHORTCUTS,
        focus: (options) => { outerContainerRef.current?.focus(options); },
        showAnnotationEditor,
        showAnnotationViewer,
    }), [goToPage, nextPage, prevPage, setZoom, totalPages, adoptSidePanelElement, showAnnotationEditor, showAnnotationViewer]);
    // ----------------------------------------------------------------------
    // Render
    // ----------------------------------------------------------------------
    // Fill the parent stage (set by PlayerSlot's NON_MEDIA_STAGE_STYLE to
    // a 16:9 box sized from the column width). The PDF canvas inside
    // scales to its own intrinsic page size and scrolls inside this
    // container if it overflows.
    const containerStyle = {
        // No explicit width — flex stretch fills the parent, and the
        // fullscreen CSS rule can shrink it to clear the sidebar toggle.
        height: '100%',
        overflow: 'auto',
        position: 'relative',
        textAlign: 'center',
        flex: '1 1 auto',
    };
    if (loadError) {
        return (_jsx(DownloadOverlay, { visible: true, message: "This document could not be displayed.", canDownload: canDownload, downloadUrl: downloadUrl || undefined, title: fileTitle, fileTypeName: fileTypeName || DEFAULT_FILE_TYPE_NAME, fileSize: fileSize, fileIconUrl: fileIconUrl }));
    }
    return (_jsxs("div", { ref: outerContainerRef, className: "hekate-document-viewer", tabIndex: -1, style: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            position: 'relative',
            outline: 'none',
        }, children: [isFullscreen && outerContainerRef.current && createPortal(_jsx(SidePanel, { isOpen: sidePanelOpen, onToggle: () => setSidePanelOpen((prev) => !prev), controlsVisible: true, contentRef: sidePanelContentRef, hasElement: sidePanelHasElement, tooltip: sidePanelTooltip }), outerContainerRef.current), _jsxs("div", { id: "player_container", ref: containerRef, style: containerStyle, children: [isLoading && (_jsx("div", { className: "spinner", style: { width: 60, height: 60 } })), _jsx("div", { ref: canvasWrapperRef, style: { position: 'relative', display: 'inline-block' }, children: _jsx("canvas", { id: "pdf-canvas", ref: canvasRef }) })] }), _jsxs("div", { id: "doc-control-container", children: [_jsx("button", { type: "button", className: "controlbtn hekate-player-btn hekate-image-zoom-fit-btn hekate-dv-collapsible hekate-dv-zoom-fit", title: "Zoom to fit", onClick: zoomToFit, children: _jsx("span", { className: "hekate-player-btn-icon", "aria-hidden": "true" }) }), _jsx("button", { className: "hekate-player-btn hekate-image-zoom-out-btn hekate-dv-collapsible hekate-dv-zoom-inout", id: "doc-zoom-out", "aria-label": "Zoom out", "data-tooltip": "Zoom out", type: "button", onClick: () => incrementZoom(-0.1), children: _jsx("span", { className: "hekate-player-btn-icon", "aria-hidden": "true" }) }), _jsxs("button", { id: "doc-zoom-100", type: "button", className: "controlbtn hekate-dv-collapsible hekate-dv-zoom-pct", onClick: () => setZoom(1.0), children: [Math.round(scale * 100), "%"] }), _jsx("button", { className: "hekate-player-btn hekate-image-zoom-in-btn hekate-dv-collapsible hekate-dv-zoom-inout", id: "doc-zoom-in", "aria-label": "Zoom in", "data-tooltip": "Zoom in", type: "button", onClick: () => incrementZoom(0.1), children: _jsx("span", { className: "hekate-player-btn-icon", "aria-hidden": "true" }) }), _jsx("span", { className: "hekate-dv-overflow-btn", children: _jsx(ViewerToolbarOverflowMenu, { items: overflowItems }) }), _jsx("div", { className: "spacer" }), _jsxs("div", { id: "doc-page-button-container", children: [_jsx("button", { id: "doc-prev-page", className: "hekate-player-btn", "aria-label": "Previous page", "data-tooltip": "Previous page", type: "button", onClick: prevPage, children: _jsx("span", { className: "fa-icon-arrow-left-solid", "aria-hidden": "true" }) }), _jsx("input", { id: "doc-page-current", type: "text", value: pageInput, onChange: (e) => setPageInput(e.target.value), onBlur: () => {
                                    const n = parseInt(pageInput, 10);
                                    if (Number.isFinite(n)) {
                                        goToPage(n);
                                    }
                                    else {
                                        setPageInput(String(currentPage));
                                    }
                                }, onKeyDown: (e) => {
                                    if (e.key === 'Enter') {
                                        e.target.blur();
                                    }
                                } }), " / ", _jsx("button", { id: "doc-page-total", onClick: () => goToPage(totalPages), children: totalPages }), _jsx("button", { id: "doc-next-page", className: "hekate-player-btn", "aria-label": "Next page", "data-tooltip": "Next page", type: "button", onClick: nextPage, children: _jsx("span", { className: "fa-icon-arrow-right-solid", "aria-hidden": "true" }) })] }), _jsx("div", { className: "spacer" }), _jsx(FullscreenButton, { isFullscreen: isFullscreen, onClick: toggleFullscreen })] })] }));
});
export default HekateDocumentViewer;
